import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import useGooglePlacesAutocomplete from '../../hooks/useGooglePlacesAutocomplete';
import { parseGooglePlaceAddress } from '../../utils/parseGooglePlaceAddress';
import {
  getClients,
  createClient,
  updateClient,
  deleteClient,
  markInvitationSent,
} from '../../services/clientsService';
import { queueInvitationEmail } from '../../services/invitationEmailService';
import {
  subscribeLenderStagedProperties,
  createLenderStagedProperty,
  deleteLenderStagedProperty,
  findFirstInvalidStagedTrancheDateRow,
  isStagedTrancheDateOrderInvalid,
  STAGED_TRANCHE_DATES_INVALID,
} from '../../services/lenderStagedPropertiesService';
import { getLenderSettings } from '../../services/lenderSettingsService';
import {
  formatStagingPrincipal,
  formatStagingPercent,
  parseMoneyLoose,
  parsePercentLoose,
  moneyFieldEditValue,
  percentFieldEditValue,
  roundMoneyToCents,
} from '../../utils/stagingAmountFormat';
import { ArrowLeft, Plus, Pencil, Trash2, Mail, Home, Upload, MessageCircle } from 'lucide-react';
import BorrowersImportModal from './BorrowersImportModal';
import VipContactsImportModal from './VipContactsImportModal';
import DirectChatModal from '../directChat/DirectChatModal';
import { buildBrokerClientConversationSpec } from '../../services/directChatService';

function stagedTranchePrincipalSum(tranches) {
  if (!Array.isArray(tranches)) return 0;
  const s = tranches.reduce((acc, tr) => {
    if (tr == null) return acc;
    if (typeof tr.amount === 'number' && Number.isFinite(tr.amount)) {
      return acc + roundMoneyToCents(tr.amount);
    }
    const p = parseMoneyLoose(tr.amount);
    if (Number.isFinite(p)) return acc + p;
    const n = Number(tr.amount);
    return acc + (Number.isFinite(n) ? roundMoneyToCents(n) : 0);
  }, 0);
  return roundMoneyToCents(s);
}

const emptyLoanTrancheRow = () => ({
  amount: '',
  startDate: '',
  interestRate: '',
  maturityDate: '',
  note: '',
});

const emptyStagedDraft = () => ({
  address: '',
  city: '',
  province: '',
  postalCode: '',
  notes: '',
  loanTranches: [emptyLoanTrancheRow()],
});

/** @param {{ createdAt?: unknown }} c */
function clientCreatedAtMs(c) {
  const v = c?.createdAt;
  if (v == null) return 0;
  if (typeof v?.toDate === 'function') return v.toDate().getTime();
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const t = Date.parse(v);
    return Number.isNaN(t) ? 0 : t;
  }
  return 0;
}

/**
 * Draft form with Google Places on the street line (Canada); other fields editable.
 */
function StagedBuildingDraftForm({
  clientId,
  draft,
  language,
  setDraftField,
  setTrancheField,
  addTrancheRow,
  removeTrancheRow,
  onPlaceParsed,
  onSave,
  onDone,
  saving,
  t,
}) {
  const autocompleteOptions = useMemo(
    () => ({
      componentRestrictions: { country: 'ca' },
      fields: ['address_components'],
    }),
    []
  );

  const handlePlace = useCallback(
    (place) => {
      onPlaceParsed(clientId, place);
    },
    [clientId, onPlaceParsed]
  );

  const addressInputRef = useGooglePlacesAutocomplete(handlePlace, autocompleteOptions);

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">{t('clients.stagedAddressAutocompleteHint')}</p>
      <input
        ref={addressInputRef}
        type="text"
        placeholder={`${t('clients.stagedAddress')} *`}
        value={draft.address}
        onChange={(e) => setDraftField(clientId, 'address', e.target.value)}
        className="w-full border rounded px-2 py-1.5 text-sm"
        autoComplete="street-address"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        <input
          type="text"
          placeholder={t('clients.stagedCity')}
          value={draft.city}
          onChange={(e) => setDraftField(clientId, 'city', e.target.value)}
          className="border rounded px-2 py-1.5 text-sm"
        />
        <input
          type="text"
          placeholder={t('clients.stagedProvince')}
          value={draft.province}
          onChange={(e) => setDraftField(clientId, 'province', e.target.value)}
          className="border rounded px-2 py-1.5 text-sm"
        />
        <input
          type="text"
          placeholder={t('clients.stagedPostal')}
          value={draft.postalCode}
          onChange={(e) => setDraftField(clientId, 'postalCode', e.target.value)}
          className="border rounded px-2 py-1.5 text-sm"
        />
        <input
          type="text"
          placeholder={t('clients.stagedNotes')}
          value={draft.notes}
          onChange={(e) => setDraftField(clientId, 'notes', e.target.value)}
          className="border rounded px-2 py-1.5 text-sm col-span-1 sm:col-span-2 lg:col-span-4"
        />
      </div>
      <div className="border-t border-gray-200 pt-2 mt-2 space-y-2">
        <p className="text-xs font-medium text-gray-700">{t('clients.stagedLoanTranchesTitle')}</p>
        <p className="text-xs text-gray-500">{t('clients.stagedLoanTranchesHelp')}</p>
        {(draft.loanTranches || [emptyLoanTrancheRow()]).map((row, idx) => {
          const dateOrderInvalid = isStagedTrancheDateOrderInvalid(row.startDate, row.maturityDate);
          return (
          <div key={idx} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 items-end bg-slate-50 rounded p-2">
            <input
              type="text"
              inputMode="decimal"
              placeholder={t('clients.stagedTranchePrincipal')}
              value={row.amount}
              onChange={(e) => setTrancheField(clientId, idx, 'amount', e.target.value)}
              onFocus={() => {
                const v = moneyFieldEditValue(row.amount);
                if (v !== row.amount) setTrancheField(clientId, idx, 'amount', v);
              }}
              onBlur={() => {
                const raw = String(row.amount || '').trim();
                if (!raw) return;
                const n = parseMoneyLoose(row.amount);
                if (Number.isFinite(n) && n >= 0) {
                  setTrancheField(clientId, idx, 'amount', formatStagingPrincipal(n, language));
                }
              }}
              className="border rounded px-2 py-1 text-sm sm:col-span-1 tabular-nums"
            />
            <input
              type="date"
              value={row.startDate}
              onChange={(e) => setTrancheField(clientId, idx, 'startDate', e.target.value)}
              className={`border rounded px-2 py-1 text-sm sm:col-span-1 ${dateOrderInvalid ? 'border-red-400 ring-1 ring-red-200' : ''}`}
            />
            <input
              type="text"
              inputMode="decimal"
              placeholder={t('clients.stagedTrancheRate')}
              value={row.interestRate}
              onChange={(e) => setTrancheField(clientId, idx, 'interestRate', e.target.value)}
              onFocus={() => {
                const v = percentFieldEditValue(row.interestRate);
                if (v !== row.interestRate) setTrancheField(clientId, idx, 'interestRate', v);
              }}
              onBlur={() => {
                const raw = String(row.interestRate || '').trim();
                if (!raw) {
                  setTrancheField(clientId, idx, 'interestRate', '');
                  return;
                }
                const n = parsePercentLoose(row.interestRate);
                if (Number.isFinite(n)) {
                  setTrancheField(clientId, idx, 'interestRate', formatStagingPercent(n, language));
                }
              }}
              className="border rounded px-2 py-1 text-sm sm:col-span-1 tabular-nums"
            />
            <input
              type="date"
              value={row.maturityDate}
              min={row.startDate || undefined}
              onChange={(e) => setTrancheField(clientId, idx, 'maturityDate', e.target.value)}
              className={`border rounded px-2 py-1 text-sm sm:col-span-1 ${dateOrderInvalid ? 'border-red-400 ring-1 ring-red-200' : ''}`}
            />
            <input
              type="text"
              placeholder={t('clients.stagedTrancheNote')}
              value={row.note}
              onChange={(e) => setTrancheField(clientId, idx, 'note', e.target.value)}
              className="border rounded px-2 py-1 text-sm col-span-1 sm:col-span-2"
            />
            {dateOrderInvalid && (
              <p className="col-span-1 sm:col-span-2 md:col-span-3 lg:col-span-6 text-xs text-red-600 -mt-0.5">
                {t('clients.stagedTrancheMaturityAfterStart')}
              </p>
            )}
            <div className="col-span-1 sm:col-span-2 md:col-span-3 lg:col-span-6 flex justify-end">
              {(draft.loanTranches || []).length > 1 && (
                <button
                  type="button"
                  onClick={() => removeTrancheRow(clientId, idx)}
                  className="text-xs text-red-600 hover:underline"
                >
                  {t('clients.stagedTrancheRemove')}
                </button>
              )}
            </div>
          </div>
          );
        })}
        <button
          type="button"
          onClick={() => addTrancheRow(clientId)}
          className="text-xs text-emerald-700 hover:underline"
        >
          {t('clients.stagedTrancheAdd')}
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <button
          type="button"
          onClick={() => onSave(clientId)}
          disabled={
            saving ||
            !String(draft.address || '').trim() ||
            (draft.loanTranches || []).some((row) =>
              isStagedTrancheDateOrderInvalid(row.startDate, row.maturityDate)
            )
          }
          className="text-sm px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
        >
          {saving ? '…' : t('clients.stagedAddButton')}
        </button>
        <button
          type="button"
          onClick={() => onDone(clientId)}
          className="text-sm px-3 py-1.5 rounded-lg bg-gray-100 text-gray-800 hover:bg-gray-200"
        >
          {t('clients.stagedDoneButton')}
        </button>
      </div>
    </div>
  );
}

const ClientsPage = ({ onBack, isPrivateLender = false, isRealEstateBroker = false }) => {
  const { currentUser, properties, userProfile } = useAuth();
  const { t, language } = useLanguage();
  const [clients, setClients] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState('');
  const [resendingId, setResendingId] = useState(null);
  const [resendConfirmMessage, setResendConfirmMessage] = useState(null);
  const [bulkInvitingAll, setBulkInvitingAll] = useState(false);
  const [bulkInviteAllResult, setBulkInviteAllResult] = useState(null);
  const [stagedList, setStagedList] = useState([]);
  const [stagedDraftByClient, setStagedDraftByClient] = useState({});
  const [stagedSavingId, setStagedSavingId] = useState(null);
  /** When true, the draft form for that borrower is visible; false = collapsed (per borrower). */
  const [stagedFormExpandedByClient, setStagedFormExpandedByClient] = useState({});
  const [showBorrowersImport, setShowBorrowersImport] = useState(false);
  const [showVipImport, setShowVipImport] = useState(false);
  const [formAddress, setFormAddress] = useState('');
  const [formSendPocketDeals, setFormSendPocketDeals] = useState(false);
  const [formSkipAutoInvite, setFormSkipAutoInvite] = useState(false);
  const [directChatModal, setDirectChatModal] = useState(null);

  const directChatUser = useMemo(() => {
    if (!currentUser) return null;
    return {
      ...currentUser,
      displayName: userProfile?.displayName || currentUser.displayName,
    };
  }, [currentUser, userProfile]);

  const handleBrokerClientPlaceSelected = useCallback((place) => {
    const parsed = parseGooglePlaceAddress(place);
    const cityProv = [parsed.city, parsed.province].filter(Boolean).join(', ').trim();
    const parts = [parsed.street, cityProv, parsed.postalCode].filter((p) => String(p || '').trim());
    setFormAddress(parts.join(', '));
  }, []);

  const brokerClientAddressAutocompleteOptions = useMemo(
    () => ({
      enabled: isRealEstateBroker && (showAdd || !!editingId),
      componentRestrictions: { country: 'ca' },
      fields: ['address_components'],
    }),
    [isRealEstateBroker, showAdd, editingId]
  );

  const brokerClientAddressInputRef = useGooglePlacesAutocomplete(
    handleBrokerClientPlaceSelected,
    brokerClientAddressAutocompleteOptions
  );

  useEffect(() => {
    if (!currentUser?.uid) return;
    const unsub = getClients(currentUser.uid, setClients);
    return () => unsub?.();
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!isPrivateLender || !currentUser?.uid) {
      setStagedList([]);
      return undefined;
    }
    return subscribeLenderStagedProperties(currentUser.uid, setStagedList);
  }, [isPrivateLender, currentUser?.uid]);

  const stagedByClient = useMemo(() => {
    const m = {};
    stagedList.forEach((row) => {
      if (!m[row.clientId]) m[row.clientId] = [];
      m[row.clientId].push(row);
    });
    return m;
  }, [stagedList]);

  /** Emprunteurs les plus récemment créés en tête (évite de défiler pour les brouillons). */
  const displayClients = useMemo(() => {
    if (!isPrivateLender || !clients.length) return clients;
    return [...clients].sort((a, b) => {
      const tb = clientCreatedAtMs(b);
      const ta = clientCreatedAtMs(a);
      if (tb !== ta) return tb - ta;
      return String(b.id).localeCompare(String(a.id));
    });
  }, [clients, isPrivateLender]);

  /** Same as per-row mail resend: token + email, account not linked. */
  const pendingBorrowerInvites = useMemo(
    () =>
      (displayClients || []).filter(
        (c) => c.invitationToken && String(c.email || '').trim() && !c.clientUserId
      ),
    [displayClients]
  );

  const countProperties = (clientId) =>
    (properties || []).filter((p) => p.clientId === clientId).length;

  const countStaged = (clientId) => (stagedByClient[clientId] || []).length;

  const getDraft = (clientId) => {
    const d = stagedDraftByClient[clientId] || emptyStagedDraft();
    if (!d.loanTranches || d.loanTranches.length === 0) {
      return { ...d, loanTranches: [emptyLoanTrancheRow()] };
    }
    return d;
  };

  const setDraftField = (clientId, field, value) => {
    setStagedDraftByClient((prev) => {
      const cur = prev[clientId] || emptyStagedDraft();
      return {
        ...prev,
        [clientId]: { ...cur, [field]: value },
      };
    });
  };

  const setTrancheField = (clientId, index, field, value) => {
    setStagedDraftByClient((prev) => {
      const cur = prev[clientId] || emptyStagedDraft();
      const rows = [...(cur.loanTranches || [emptyLoanTrancheRow()])];
      rows[index] = { ...rows[index], [field]: value };
      return { ...prev, [clientId]: { ...cur, loanTranches: rows } };
    });
  };

  const addTrancheRow = (clientId) => {
    setStagedDraftByClient((prev) => {
      const cur = prev[clientId] || emptyStagedDraft();
      const rows = [...(cur.loanTranches || []), emptyLoanTrancheRow()];
      return { ...prev, [clientId]: { ...cur, loanTranches: rows } };
    });
  };

  const removeTrancheRow = (clientId, index) => {
    setStagedDraftByClient((prev) => {
      const cur = prev[clientId] || emptyStagedDraft();
      const rows = (cur.loanTranches || []).filter((_, i) => i !== index);
      return {
        ...prev,
        [clientId]: { ...cur, loanTranches: rows.length ? rows : [emptyLoanTrancheRow()] },
      };
    });
  };

  const applyGooglePlaceToStagedDraft = useCallback((clientId, place) => {
    const parsed = parseGooglePlaceAddress(place);
    setStagedDraftByClient((prev) => ({
      ...prev,
      [clientId]: {
        ...(prev[clientId] || emptyStagedDraft()),
        address: parsed.street || (prev[clientId] || emptyStagedDraft()).address,
        city: parsed.city || '',
        province: parsed.province || '',
        postalCode: parsed.postalCode || '',
      },
    }));
  }, []);

  const handleAddStaged = async (clientId) => {
    if (!currentUser?.uid) return;
    const d = getDraft(clientId);
    if (!String(d.address || '').trim()) return;
    if (findFirstInvalidStagedTrancheDateRow(d.loanTranches) >= 0) {
      alert(t('clients.stagedTrancheMaturityAfterStart'));
      return;
    }
    setStagedSavingId(clientId);
    try {
      await createLenderStagedProperty({
        lenderUid: currentUser.uid,
        clientId,
        address: d.address,
        city: d.city,
        province: d.province,
        postalCode: d.postalCode,
        notes: d.notes,
        loanTranches: d.loanTranches,
      });
      setStagedDraftByClient((prev) => ({ ...prev, [clientId]: emptyStagedDraft() }));
      setStagedFormExpandedByClient((prev) => ({ ...prev, [clientId]: false }));
    } catch (e) {
      console.error(e);
      if (e?.message === STAGED_TRANCHE_DATES_INVALID) {
        alert(t('clients.stagedTrancheMaturityAfterStart'));
      } else {
        alert((e?.message || t('share.error')) + '');
      }
    } finally {
      setStagedSavingId(null);
    }
  };

  const inviteEmailPayload = (clientId, emailTo, clientName, invitationLink, options = {}) => ({
    to: emailTo,
    clientName: clientName || undefined,
    invitationLink,
    clientId,
    ...(isPrivateLender ? { invitationKind: 'private_lender_borrower' } : {}),
    ...(isPrivateLender ? { invitationScenario: 'migration' } : {}),
    ...(options?.invitationScenario ? { invitationScenario: options.invitationScenario } : {}),
  });

  const handleSaveEdit = async () => {
    if (editingId) {
      const prev = clients.find((x) => x.id === editingId);
      const patch = {
        name: formName,
        email: formEmail,
        ...(isRealEstateBroker
          ? {
              address: formAddress.trim(),
              sendPocketDeals: formSendPocketDeals,
              ...(formSendPocketDeals && !prev?.sendPocketDeals
                ? {
                    pocketDealsOptInAt: new Date(),
                    pocketDealsOptInSource: 'checkbox',
                  }
                : {}),
            }
          : {}),
      };
      await updateClient(editingId, patch);
      setEditingId(null);
    } else if (showAdd) {
      setSendingInvite(true);
      setLastInviteLink('');
      try {
        const { clientId, invitationLink } = await createClient(currentUser.uid, {
          name: formName,
          email: formEmail,
          brokerDisplayName: currentUser.displayName || '',
          ...(isRealEstateBroker
            ? {
                address: formAddress.trim(),
                sendPocketDeals: formSendPocketDeals,
                skipAutoInvite: formSkipAutoInvite,
                pocketDealsOptInSource: formSendPocketDeals ? 'checkbox' : undefined,
              }
            : {}),
        });
        const emailTo = (formEmail || '').trim().toLowerCase();
        let skipAutoInvite = isRealEstateBroker ? formSkipAutoInvite : false;
        if (!isRealEstateBroker) {
          try {
            const ls = await getLenderSettings(currentUser.uid);
            skipAutoInvite = ls?.fundInConfiguration === true;
          } catch {
            /* ignore */
          }
        }
        if (emailTo && !skipAutoInvite) {
          try {
            await queueInvitationEmail(
              currentUser.uid,
              inviteEmailPayload(clientId, emailTo, formName, invitationLink, {
                invitationScenario: isPrivateLender ? 'migration' : undefined,
              })
            );
            await markInvitationSent(clientId);
          } catch (e) {
            console.error('Queue invitation email:', e);
            alert(t('clients.inviteQueueError') + ' ' + (e?.message || e));
          }
        }
        setLastInviteLink(invitationLink);
        setFormName('');
        setFormEmail('');
        setFormAddress('');
        setFormSendPocketDeals(false);
        setFormSkipAutoInvite(false);
      } finally {
        setSendingInvite(false);
      }
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm(t('clients.deleteConfirm'))) return;
    await deleteClient(clientId);
  };

  const buildInvitationLink = (token) => {
    if (!token || typeof window === 'undefined') return '';
    return `${window.location.origin}${window.location.pathname}#/signup?invitation=${token}`;
  };

  const handleResendInvitation = async (c) => {
    if (!c.invitationToken || !c.email) return;
    setResendingId(c.id);
    try {
      const invitationLink = buildInvitationLink(c.invitationToken);
      await queueInvitationEmail(
        currentUser.uid,
        inviteEmailPayload(c.id, (c.email || '').trim().toLowerCase(), c.name, invitationLink)
      );
      await markInvitationSent(c.id);
      setResendConfirmMessage(c.name || c.email || c.id);
      setTimeout(() => setResendConfirmMessage(null), 5000);
    } catch (e) {
      console.error('Resend invitation:', e);
      alert(t('clients.inviteQueueError') + ' ' + (e?.message || e));
    } finally {
      setResendingId(null);
    }
  };

  const handleBulkInviteAllClients = async () => {
    if (!currentUser?.uid || bulkInvitingAll || pendingBorrowerInvites.length === 0) return;
    if (!window.confirm(t('clients.bulkInviteAllConfirm'))) return;
    setBulkInvitingAll(true);
    setBulkInviteAllResult(null);
    let ok = 0;
    let failed = 0;
    try {
      for (const c of pendingBorrowerInvites) {
        try {
          const invitationLink = buildInvitationLink(c.invitationToken);
          await queueInvitationEmail(
            currentUser.uid,
            inviteEmailPayload(c.id, (c.email || '').trim().toLowerCase(), c.name, invitationLink)
          );
          await markInvitationSent(c.id);
          ok += 1;
        } catch (e) {
          console.error('Bulk invite client', c.id, e);
          failed += 1;
        }
      }
      setBulkInviteAllResult({ ok, failed });
      setTimeout(() => setBulkInviteAllResult(null), 10000);
    } finally {
      setBulkInvitingAll(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setFormName(c.name || '');
    setFormEmail(c.email || '');
    setFormAddress(c.address || '');
    setFormSendPocketDeals(c.sendPocketDeals === true);
    setFormSkipAutoInvite(false);
    setShowAdd(false);
    setLastInviteLink('');
  };

  const startAdd = () => {
    setShowAdd(true);
    setEditingId(null);
    setFormName('');
    setFormEmail('');
    setFormAddress('');
    setFormSendPocketDeals(false);
    setFormSkipAutoInvite(false);
    setLastInviteLink('');
  };

  const titleKey = isPrivateLender ? 'clients.titleBorrowers' : 'clients.title';
  const addKey = isPrivateLender ? 'clients.addBorrower' : 'clients.add';
  const noClientsKey = isPrivateLender ? 'clients.noBorrowers' : 'clients.noClients';
  const inviteCopyKey = isPrivateLender ? 'clients.inviteLinkCopyBorrower' : 'clients.inviteLinkCopy';
  const propertiesLinkedKey = isPrivateLender ? 'clients.propertiesLinkedBorrower' : 'clients.propertiesLinked';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={onBack}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 shrink-0"
            >
              <ArrowLeft size={20} />
              {t('back')}
            </button>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 truncate">{t(titleKey)}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            {isPrivateLender && (
              <button
                type="button"
                onClick={() => setShowBorrowersImport(true)}
                className="flex items-center gap-2 border border-blue-600 text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-50 text-sm"
              >
                <Upload size={18} />
                {t('clients.importBorrowersButton')}
              </button>
            )}
            {isRealEstateBroker && (
              <button
                type="button"
                onClick={() => setShowVipImport(true)}
                className="flex items-center gap-2 border border-violet-600 text-violet-700 px-3 py-2 rounded-lg hover:bg-violet-50 text-sm"
              >
                <Upload size={18} />
                {t('clients.importVipButton')}
              </button>
            )}
            <button
              type="button"
              onClick={startAdd}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              <Plus size={18} />
              {t(addKey)}
            </button>
          </div>
        </div>

        {resendConfirmMessage && (
          <div className="mb-4 p-4 bg-green-100 border border-green-300 rounded-lg text-green-800 flex items-center justify-between">
            <p className="font-medium">
              {t('clients.invitationResentConfirm')} {resendConfirmMessage}
            </p>
            <button
              type="button"
              onClick={() => setResendConfirmMessage(null)}
              className="text-green-600 hover:text-green-800 text-xl leading-none"
              aria-label={t('cancel')}
            >
              ×
            </button>
          </div>
        )}
        {bulkInviteAllResult && (
          <div
            className={`mb-4 p-3 rounded-lg border text-sm ${
              bulkInviteAllResult.failed > 0
                ? 'bg-amber-50 border-amber-200 text-amber-900'
                : 'bg-green-50 border-green-200 text-green-800'
            }`}
          >
            {bulkInviteAllResult.failed > 0
              ? t('clients.bulkInviteAllResultPartial')
                  .replace('{ok}', String(bulkInviteAllResult.ok))
                  .replace('{failed}', String(bulkInviteAllResult.failed))
              : t('clients.bulkInviteAllResultOk').replace('{n}', String(bulkInviteAllResult.ok))}
          </div>
        )}
        {currentUser?.uid && pendingBorrowerInvites.length > 0 && (
          <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-xl border border-indigo-100 bg-indigo-50/60 px-3 py-2.5">
            <p className="text-sm text-indigo-950">
              {t(isPrivateLender ? 'clients.bulkInviteAllSummaryBorrower' : 'clients.bulkInviteAllSummary').replace(
                '{n}',
                String(pendingBorrowerInvites.length)
              )}
            </p>
            <button
              type="button"
              onClick={handleBulkInviteAllClients}
              disabled={bulkInvitingAll}
              className="inline-flex items-center justify-center gap-2 shrink-0 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            >
              {bulkInvitingAll ? (
                t('clients.bulkInviteAllSending')
              ) : (
                <>
                  <Mail className="w-4 h-4 shrink-0" />
                  {t('clients.bulkInviteAllButton')}
                </>
              )}
            </button>
          </div>
        )}

        {(showAdd || editingId) && (
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <h2 className="font-medium mb-3">
              {editingId ? t('clients.edit') : t('clients.add')}
            </h2>
            <div className="grid gap-3 grid-cols-1">
              <input
                type="text"
                placeholder={t('clients.name')}
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="border rounded px-3 py-2"
              />
              <input
                type="email"
                placeholder={t('clients.email')}
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                className="border rounded px-3 py-2"
              />
            </div>
            {isRealEstateBroker && (
              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-0.5">{t('clients.address')}</label>
                  <p className="text-xs text-gray-500 mb-1">{t('companies.addressAutocompleteHint')}</p>
                  <input
                    ref={brokerClientAddressInputRef}
                    type="text"
                    placeholder={t('clients.address')}
                    value={formAddress}
                    onChange={(e) => setFormAddress(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    autoComplete="street-address"
                  />
                </div>
                <p className="text-xs text-gray-500">{t('clients.addressOptionalHint')}</p>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={formSendPocketDeals}
                    onChange={(e) => setFormSendPocketDeals(e.target.checked)}
                  />
                  <span className="text-sm text-gray-800">{t('clients.sendPocketDeals')}</span>
                </label>
                {!editingId && (
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={formSkipAutoInvite}
                      onChange={(e) => setFormSkipAutoInvite(e.target.checked)}
                    />
                    <span className="text-sm text-gray-800">{t('clients.skipAutoInvite')}</span>
                  </label>
                )}
              </div>
            )}
            {lastInviteLink && (
              <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded text-sm">
                <p className="font-medium text-green-800 mb-1">{t('clients.inviteLinkCreated')}</p>
                <a href={lastInviteLink} className="text-blue-600 break-all" target="_blank" rel="noopener noreferrer">
                  {lastInviteLink}
                </a>
                <p className="mt-1 text-gray-600">{t(inviteCopyKey)}</p>
                <button
                  type="button"
                  onClick={() => { navigator.clipboard.writeText(lastInviteLink); setLastInviteLink(''); }}
                  className="mt-2 text-blue-600 underline"
                >
                  {t('clients.copyLink')}
                </button>
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={sendingInvite}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {sendingInvite ? t('clients.sending') : t('save')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setShowAdd(false);
                  setLastInviteLink('');
                }}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden">
          {clients.length === 0 && !showAdd ? (
            <p className="p-6 text-gray-500 text-center">{t(noClientsKey)}</p>
          ) : (
            <ul className="divide-y divide-gray-200">
              {displayClients.map((c) => (
                <li key={c.id} className="p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-medium text-gray-800">{c.name || '—'}</p>
                      <p className="text-sm text-gray-500">{c.email || '—'}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {t(propertiesLinkedKey)}: {countProperties(c.id)}
                        {isPrivateLender && (
                          <>
                            {' · '}
                            {t('clients.stagedBuildingsCount')}: {countStaged(c.id)}
                          </>
                        )}
                      </p>
                      {isRealEstateBroker && (
                        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                          <span
                            className={`px-2 py-0.5 rounded-full ${
                              c.clientUserId ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'
                            }`}
                          >
                            {c.clientUserId ? t('clients.vipOnRentalyzer') : t('clients.vipPocketOnly')}
                          </span>
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={c.sendPocketDeals === true}
                              onChange={async (e) => {
                                const v = e.target.checked;
                                try {
                                  await updateClient(c.id, {
                                    sendPocketDeals: v,
                                    ...(v
                                      ? { pocketDealsOptInAt: new Date(), pocketDealsOptInSource: 'checkbox' }
                                      : { pocketDealsOptInAt: null, pocketDealsOptInSource: null }),
                                  });
                                } catch (err) {
                                  console.error(err);
                                }
                              }}
                            />
                            <span>{t('clients.sendPocketDeals')}</span>
                          </label>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {c.invitationToken && c.email && !c.clientUserId && (
                        <button
                          type="button"
                          onClick={() => handleResendInvitation(c)}
                          disabled={resendingId === c.id}
                          className="p-2 text-amber-600 hover:bg-amber-50 rounded disabled:opacity-50"
                          title={t('clients.resendInvitation')}
                          aria-label={t('clients.resendInvitation')}
                        >
                          <Mail size={18} />
                        </button>
                      )}
                      {c.clientUserId && currentUser?.uid && (
                        <button
                          type="button"
                          onClick={() => {
                            const spec = buildBrokerClientConversationSpec(c.id, currentUser.uid, c.clientUserId);
                            setDirectChatModal({
                              conversationId: spec.conversationId,
                              ensureSpec: {
                                participantUids: spec.participantUids,
                                kind: spec.kind,
                                context: spec.context,
                              },
                              title: t('clients.messagePrivate'),
                              subtitle: c.name || c.email || spec.conversationId,
                            });
                          }}
                          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded"
                          title={t('clients.messagePrivate')}
                          aria-label={t('clients.messagePrivate')}
                        >
                          <MessageCircle size={18} />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => startEdit(c)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        aria-label={t('clients.edit')}
                      >
                        <Pencil size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(c.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        aria-label={t('clients.delete')}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  {isPrivateLender && (
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <p className="text-xs font-semibold text-gray-700 flex items-center gap-1 mb-2">
                        <Home className="w-3.5 h-3.5" />
                        {t('clients.stagedSectionTitle')}
                      </p>
                      <p className="text-xs text-gray-500 mb-2">{t('clients.stagedSectionHelp')}</p>
                      {(stagedByClient[c.id] || []).length > 0 && (
                        <ul className="space-y-1 mb-3">
                          {(stagedByClient[c.id] || []).map((s) => (
                            <li
                              key={s.id}
                              className="flex items-center justify-between gap-2 text-sm bg-slate-50 rounded px-2 py-1.5"
                            >
                              <span className="text-gray-800 truncate">
                                <span className="block">
                                  {[s.address, s.city, s.province, s.postalCode].filter(Boolean).join(', ') || s.address}
                                </span>
                                {Array.isArray(s.loanTranches) && s.loanTranches.length > 0 && (
                                  <>
                                    <span className="block text-xs text-gray-500 mt-0.5">
                                      {t('clients.stagedTranchesSummary').replace(
                                        '{count}',
                                        String(s.loanTranches.length)
                                      ).replace(
                                        '{total}',
                                        formatStagingPrincipal(stagedTranchePrincipalSum(s.loanTranches), language)
                                      )}
                                    </span>
                                    {(() => {
                                      const rates = (s.loanTranches || [])
                                        .map((tr) => {
                                          const raw = tr?.interestRate;
                                          if (raw == null || raw === '') return null;
                                          const n =
                                            typeof raw === 'number' ? raw : parsePercentLoose(raw);
                                          return Number.isFinite(n) ? formatStagingPercent(n, language) : null;
                                        })
                                        .filter(Boolean);
                                      if (rates.length === 0) return null;
                                      return (
                                        <span className="block text-xs text-gray-500 tabular-nums">
                                          {rates.join(' · ')}
                                        </span>
                                      );
                                    })()}
                                  </>
                                )}
                              </span>
                              {!c.clientUserId && (
                                <button
                                  type="button"
                                  onClick={() => deleteLenderStagedProperty(s.id).catch(console.error)}
                                  className="text-red-600 text-xs shrink-0 hover:underline"
                                >
                                  {t('clients.stagedDelete')}
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      {!c.clientUserId ? (
                        <div className="space-y-2">
                          {!stagedFormExpandedByClient[c.id] ? (
                            <button
                              type="button"
                              onClick={() =>
                                setStagedFormExpandedByClient((prev) => ({ ...prev, [c.id]: true }))
                              }
                              className="text-sm px-3 py-1.5 border border-emerald-600 text-emerald-800 rounded-lg hover:bg-emerald-50"
                            >
                              {t('clients.stagedExpandAddButton')}
                            </button>
                          ) : (
                            <>
                              <p className="text-xs font-medium text-gray-600 mb-1">
                                {t('clients.stagedFormOpenLabel')}
                              </p>
                              <StagedBuildingDraftForm
                                clientId={c.id}
                                draft={getDraft(c.id)}
                                language={language}
                                setDraftField={setDraftField}
                                setTrancheField={setTrancheField}
                                addTrancheRow={addTrancheRow}
                                removeTrancheRow={removeTrancheRow}
                                onPlaceParsed={applyGooglePlaceToStagedDraft}
                                onSave={handleAddStaged}
                                onDone={(clientId) =>
                                  setStagedFormExpandedByClient((prev) => ({ ...prev, [clientId]: false }))
                                }
                                saving={stagedSavingId === c.id}
                                t={t}
                              />
                            </>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500">{t('clients.stagedAfterSignupNote')}</p>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {isPrivateLender && currentUser?.uid && (
        <BorrowersImportModal
          open={showBorrowersImport}
          onClose={() => setShowBorrowersImport(false)}
          lenderUid={currentUser.uid}
          displayName={currentUser?.displayName}
          existingClients={clients}
          t={t}
          language={language}
        />
      )}
      {isRealEstateBroker && currentUser?.uid && (
        <VipContactsImportModal
          open={showVipImport}
          onClose={() => setShowVipImport(false)}
          brokerUid={currentUser.uid}
          displayName={currentUser?.displayName}
          existingClients={clients}
          t={t}
          language={language}
          onImported={() => {}}
        />
      )}
      {directChatModal && directChatUser && (
        <DirectChatModal
          onClose={() => setDirectChatModal(null)}
          conversationId={directChatModal.conversationId}
          ensureSpec={directChatModal.ensureSpec}
          title={directChatModal.title}
          subtitle={directChatModal.subtitle}
          currentUser={directChatUser}
        />
      )}
    </div>
  );
};

export default ClientsPage;
